/**
 * The dense data table. One component; the product list, batch list and import
 * error table are instances of it.
 *
 * ## `fr` is unrepresentable here, and that is the whole point of the column type
 *
 * `components.md`: *"Use **fixed px tracks**, not `fr`, when each row is its own
 * grid — `fr` resolves per row against content and columns drift between rows."*
 *
 * That reads like a styling preference and is a defect. It is also **invisible
 * until real data arrives**: with three fixture rows of similar length nothing
 * drifts, and with nine hundred real product names — arriving in German — every
 * row's columns land somewhere slightly different and the table stops being
 * scannable. A rule stated in prose would be re-derived wrongly by the first
 * person who finds `1fr` more convenient.
 *
 * So {@link Column} takes `width: number`, in pixels. There is no string form,
 * so `"1fr"` is not a thing that can be passed, and the grid template is built
 * here rather than authored per screen.
 *
 * ## Row height is fixed for a reason that is not aesthetics
 *
 * 47px, always. Fixed height is what lets virtualisation and skeletons agree —
 * a windowed list computes its scroll extent from a row height it has to know
 * in advance, and a skeleton that does not match the real geometry produces a
 * visible jump on load. c34 added the windowing; the geometry was settled in
 * c24 so that it could.
 *
 * ## Ineligible rows stay visible
 *
 * They are not filtered out and not greyed into illegibility: they stay, they
 * cannot be selected, and **the reason sits in the primary cell** where the eye
 * already is. Hiding them answers "why is my product missing" with silence, and
 * that question arrives as a support ticket.
 *
 * ## This component holds no state, and that is deliberate (c34)
 *
 * Scroll position, selection and cursor are all props, and every rule about them
 * lives in a pure module beside this one — `window.ts`, `selection.ts`,
 * `keyboard.ts`. Neither `packages/ui` nor `apps/app` runs jsdom, so a hook here
 * would put the table's actual decisions in the one place this repo's suites
 * cannot reach; the pure modules are probed by calling them, and c38's Playwright
 * work exercises the real scrolling and the real keystrokes. It also keeps
 * `DataTable({...})` callable as a plain function, which is how the c24 tests
 * obtain its element tree and how TypeScript infers `Row` correctly.
 */
import { createElement } from "react";
import { TABLE_ROW_HEIGHT } from "./geometry.js";
import { tableKeyAction } from "./keyboard.js";
import { isRowSelected } from "./selection.js";
import { rowWindow } from "./window.js";
/** The grid template, built from px widths so no caller can author `fr`. */
function templateFor(columns) {
    return ["32px", ...columns.map((c) => `${c.width}px`)].join(" ");
}
/** `↑` / `↓` next to the sorted header. Direction is stated, never implied by order alone. */
function sortMarker(order) {
    return order === "asc" ? " ↑" : " ↓";
}
export function DataTable({ columns, rows, selection, selected, onToggle, onSelectAll, allSelected = false, selectionBar, caption, sort, onSort, scrollTop, viewportHeight, onScroll, cursor, onCursorChange, onSelectionChange, onOpen, onFocusSearch, }) {
    const gridTemplateColumns = templateFor(columns);
    const folding = columns.filter((c) => c.foldsNarrow === true);
    const windowed = scrollTop !== undefined && viewportHeight !== undefined;
    const slice = windowed
        ? rowWindow(scrollTop, viewportHeight, rows.length)
        : { start: 0, end: rows.length, padTop: 0, padBottom: 0 };
    const visible = rows.slice(slice.start, slice.end);
    const isSelected = (id) => selection !== undefined ? isRowSelected(selection, id) : (selected?.has(id) ?? false);
    const header = createElement("div", { className: "pp-table-head", style: { gridTemplateColumns }, role: "row" }, createElement("span", { className: "pp-table-cell", role: "columnheader" }, onSelectAll === undefined
        ? ""
        : createElement("input", {
            type: "checkbox",
            checked: allSelected,
            onChange: (e) => onSelectAll(e.target.checked),
            // Named for what it does, which is not what it looks like: the box
            // sits above a hundred loaded rows and selects nine hundred.
            "aria-label": "Select every row matching the current filter",
        })), ...columns.map((c) => {
        const sortKey = c.sortKey;
        const active = sort !== undefined && sortKey !== undefined && sort.key === sortKey;
        const label = active ? c.header + sortMarker(sort.order) : c.header;
        return createElement("span", {
            key: c.key,
            className: "pp-table-cell",
            role: "columnheader",
            "data-folds": c.foldsNarrow === true ? "true" : undefined,
            "aria-sort": active
                ? sort.order === "asc"
                    ? "ascending"
                    : "descending"
                : undefined,
        }, sortKey !== undefined && onSort !== undefined
            ? createElement("button", {
                type: "button",
                className: "pp-table-sort",
                onClick: () => onSort(sortKey),
            }, label)
            : label);
    }));
    const body = visible.map((row, offset) => {
        const index = slice.start + offset;
        const rowSelected = isSelected(row.id);
        const blocked = row.ineligible !== undefined;
        return createElement("div", {
            key: row.id,
            className: "pp-table-row",
            style: { gridTemplateColumns },
            role: "row",
            "data-selected": rowSelected ? "true" : undefined,
            "data-ineligible": blocked ? "true" : undefined,
            "data-active": cursor !== undefined && cursor.active === index ? "true" : undefined,
            "aria-disabled": blocked ? "true" : undefined,
        }, createElement("span", { className: "pp-table-cell", role: "gridcell" }, createElement("input", {
            type: "checkbox",
            checked: rowSelected,
            // Visible and unselectable — never removed from the DOM, so the row
            // still reads as a row rather than as a gap.
            disabled: blocked,
            onChange: onToggle === undefined ? undefined : () => onToggle(row.id),
            "aria-label": `Select ${row.id}`,
        })), ...columns.map((c) => createElement("span", {
            key: c.key,
            className: "pp-table-cell",
            role: "gridcell",
            // No click handler here, deliberately: **the screen supplies the mouse
            // affordance from inside its own `render`, and which element it uses
            // depends on what opening the row does.**
            //
            //   - Opening **navigates** (the product list) → a `<Link>`, so it carries
            //     an href a user can middle-click, copy and read in the status bar.
            //   - Opening **discloses** something in place (the audit log's before/after
            //     diff, which is `useState` on the screen) → a `<button>` carrying
            //     `aria-expanded`. A link that navigates nowhere would be worse
            //     semantics, not better.
            //
            // A clickable `<span>` is never either. Amended at c37c, where the audit log
            // supplied neither and its diff turned out to be unopenable by anyone.
            //
            // The keyboard half is this component's, gated on `cursor` — see
            // `KeyboardProps`. Both halves are owed, and the split is why a screen can
            // ship with one and no check notices.
            "data-folds": c.foldsNarrow === true ? "true" : undefined,
        }, c.render(row), 
        // The reason travels with the identity, in the cell the eye is
        // already on — not in a tooltip, and not in a legend.
        c.primary === true && row.ineligible !== undefined
            ? createElement("span", { className: "pp-table-reason" }, row.ineligible)
            : null, 
        // Below 1024px the folding columns are hidden and their values land
        // here as a mono sub-line. `components.md` says they *fold into the
        // primary cell*, not that they disappear — a GTIN that vanishes on a
        // tablet takes the row's identity with it. The sub-line is rendered
        // always and shown by the media query, so nothing depends on
        // measuring the viewport in JavaScript.
        c.primary === true && folding.length > 0
            ? createElement("span", { className: "pp-table-fold" }, ...folding.map((f) => createElement("span", { key: f.key, className: "pp-table-fold-part" }, (f.renderFolded ?? f.render)(row))))
            : null)));
    });
    const bodyProps = { className: "pp-table-body" };
    if (windowed) {
        bodyProps["style"] = { height: `${viewportHeight}px`, overflowY: "auto" };
        bodyProps["onScroll"] =
            onScroll === undefined
                ? undefined
                : (e) => onScroll(e.currentTarget.scrollTop);
    }
    const bodyChildren = [];
    if (slice.padTop > 0) {
        bodyChildren.push(createElement("div", {
            key: "pad-top",
            className: "pp-table-pad",
            style: { height: `${slice.padTop}px` },
            "aria-hidden": "true",
        }));
    }
    bodyChildren.push(...body);
    if (slice.padBottom > 0) {
        bodyChildren.push(createElement("div", {
            key: "pad-bottom",
            className: "pp-table-pad",
            style: { height: `${slice.padBottom}px` },
            "aria-hidden": "true",
        }));
    }
    const onKeyDown = cursor === undefined
        ? undefined
        : (e) => {
            // A key pressed on an interactive descendant belongs to that descendant.
            //
            // The screen's mouse affordance — a `<Link>` or a disclosure `<button>` — is a
            // focusable element **inside** this grid, so its key events bubble to this
            // handler. Without this guard, `Enter` on a focused link runs the table's own
            // Enter branch, which calls `preventDefault()` and **suppresses the link's own
            // activation**: the row does not open, and the one thing the user pressed Enter
            // on is the thing that stops working. Found at c37c while giving the audit log
            // a mouse path; `ProductList` had carried it latently since its link landed.
            if (e.target !== e.currentTarget)
                return;
            const result = tableKeyAction({ key: e.key, shiftKey: e.shiftKey }, cursor, 
            // The selection the table **renders** is the one the model reasons
            // about. Passing anything else here is how the two came apart.
            selection, rows);
            if (!result.handled)
                return;
            e.preventDefault();
            if (result.cursor !== null)
                onCursorChange?.(result.cursor);
            if (result.selection !== undefined)
                onSelectionChange?.(result.selection);
            if (result.open !== undefined)
                onOpen?.(result.open);
            if (result.focusSearch === true)
                onFocusSearch?.();
        };
    return createElement("div", {
        className: "pp-table",
        role: "grid",
        "aria-label": caption,
        // Focusable so the bindings above have somewhere to arrive. `0` rather
        // than `-1`: the table is a stop on the tab order, which is what makes the
        // keyboard model reachable without a mouse in the first place.
        tabIndex: cursor === undefined ? undefined : 0,
        onKeyDown,
    }, selectionBar, header, createElement("div", bodyProps, ...bodyChildren));
}
/** Exported so the windowing and the skeletons compute from the same constant. */
export { TABLE_ROW_HEIGHT };
export function TableSelectionBar({ count, children, }) {
    return createElement("div", { className: "pp-table-selection", role: "status" }, createElement("span", null, count === null ? "Counting the selection…" : `${count} selected`), children);
}
//# sourceMappingURL=DataTable.js.map